import { Container, ContainerMetadata } from '../types/canvas.types';
import { generateComponentId } from './IdGenerator';
import { containerStore } from './containerRegistryBridge';

export function isContainerNested(container: Container | { id: string; metadata?: ContainerMetadata }): boolean {
    if (container.metadata?.isNested !== undefined) {
        return container.metadata.isNested;
    }
    
    if ('id' in container) {
        const metadata = containerStore.getContainerMetadata(container.id);
        return metadata?.isNested === true;
    }
    
    return false;
}

export function createContainer( name: string, order: number, isNested: boolean = false, parentContainerId?: string, nestingLevel: number = 0): Container {
    return {
        id: generateComponentId(isNested ? 'nested-container' : 'container'),
        name,
        order,
        metadata: {
            type: isNested ? 'nested-container' : 'container',
            isNested,
            parentContainerId,
            nestingLevel
        },
        configuration: {
            width: '100%',
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
            widthLocked: false,
            behaveAsFilter: false,
            collapsible: false
        },
        content: {
            type: 'widget',
            componentId: 'empty',
            'widget-type': 'contentBlock',
            nestedContainers: [],
            items: []
        },
        usageInfo: {
            usedComponents: []
        }
    };
}

export function findContainer(containers: Container[], containerId: string): Container | null {
    for (const container of containers) {
        if (container.id === containerId) {
            return container;
        }
        
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const found = findContainer(container.content.nestedContainers, containerId);
            if (found) return found;
        }
    }
    return null;
}

export const organizeNestedContainersIntoRows = (nestedContainers: Container[]): Container[][] => {
    const rowMap: Record<number, Container[]> = {};
    
    nestedContainers.forEach(container => {
        const rowIndex = container.metadata?.rowIndex ?? 0;
        if (!rowMap[rowIndex]) {
            rowMap[rowIndex] = [];
        }
        rowMap[rowIndex].push(container);
    });
    
    Object.keys(rowMap).forEach(rowIdx => {
        rowMap[Number(rowIdx)].sort((a, b) => a.order - b.order);
    });
    
    const rowKeys = Object.keys(rowMap)
        .map(Number)
        .sort((a, b) => a - b);
    
    return rowKeys.map(key => rowMap[key] || []);
};

export function findContainerPath(
    containers: Container[],
    targetId: string,
    currentPath: string[] = []
): string[] | null {
    for (const container of containers) {
        const containerPath = [...currentPath, container.id];
        
        if (container.id === targetId) {
            return containerPath;
        }
        
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const nestedPath = findContainerPath(
                container.content.nestedContainers,
                targetId,
                containerPath
            );
            if (nestedPath) return nestedPath;
        }
    }
    return null;
}

export function getParentContainerId(
    containers: Container[],
    nestedContainerId: string
): string | null {
    for (const container of containers) {
        if (container.metadata?.parentContainerId === nestedContainerId) {
            return container.metadata.parentContainerId;
        }
        
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const nestedIds = container.content.nestedContainers.map(nc => nc.id);
            if (nestedIds.includes(nestedContainerId)) {
                return container.id;
            }
                
            const parentId = getParentContainerId(container.content.nestedContainers, nestedContainerId);
            if (parentId) return parentId;
        }
    }
    return null;
}

export function updateContainerMetadata(
    container: Container,
    updates: Partial<ContainerMetadata>
): Container {
    return {
        ...container,
        metadata: {
            ...container.metadata,
            ...updates
        } as ContainerMetadata
    };
}

export const findContainerRecursively = (containers: Container[], targetId: string): Container | null => {
    for (const container of containers) {
        if (container.id === targetId) {
            return container;
        }
        
        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
            const found = findContainerRecursively(container.content.nestedContainers, targetId);
            if (found) return found;
        }
    }
    return null;
};